import PropTypes from "prop-types"
import { useEffect, useState } from "react";
import { InputAdornment, TextField, FormControlLabel, Checkbox } from "@material-ui/core";
import { NumericFormat } from "react-number-format";
import SelectGrupoContabil from "../../../../components/Financial/SelectGrupoContabil";
import SelectSubgrupoContabil from "../../../../components/Financial/SelectSubgrupoContabil";
import { formatInputValuesToDefault, ACCOUNT_GROUP_TYPE, formatNumber, BillingInstallmentPropTypes, AccountGroupPropTypes, AccountSubgroupPropTypes, CONTAS_A_RECEBER_VALUES_DEFAULT } from "../../../../utils/financialHelper";
import { MaskedTextField } from "../../../../components/FormFields";
import CrudFormPage from "../../../../components/Financial/CrudFormPage";
import InstallmentList from "../../../../components/Financial/InstallmentList";
import useBillingInstallments, { BILLING_RECEIVABLE_ID } from "../../../../hooks/useBillingInstallments";

const FormContasAReceber = ({ values, setValues, isDelete: isDisabled, isLoadingSubmit = false }) => {

  const [paymentFieldsRequired, setPaymentFieldsRequired] = useState(false)

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues({ ...values, [name]: formatInputValuesToDefault(value) });
  }

  const isPaymentFieldsRequired = (formValues) => {
    const hasAmountPayedValue = !!formValues.amountPayed;
    const hasPaymentDateValue = !!formValues.receiptDate;
    return hasAmountPayedValue || hasPaymentDateValue;
  };

  useEffect(() => {
    setPaymentFieldsRequired(isPaymentFieldsRequired(values))
  }, []);

  const handleChangePaid = (e) => {
    const { name, value } = e.target;
    // React doesn't update the value on change at this point, we have to simulate it
    const updatedValues = { ...values, [name]: value };
    setPaymentFieldsRequired(isPaymentFieldsRequired(updatedValues));
    handleChange(e);
  };

  const {
    installmentsCount,
    handleChangeInstallmentCounts,
  } = useBillingInstallments({
    values,
    setValues,
    installmentIdKey: BILLING_RECEIVABLE_ID
  })

  return (
    <CrudFormPage isLoadingSubmit={isLoadingSubmit}>
      <div className='linha-campos'>
        <TextField
          label="Nome"
          id="name"
          name='name'
          value={values.name}
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          onChange={handleChange}
          required
          fullWidth
          disabled={isDisabled}
        />
      </div>
      <div className='linha-campos'>
        <SelectGrupoContabil setValues={setValues} accountGroup={values?.accountGroup ?? null} isDelete={isDisabled} type={ACCOUNT_GROUP_TYPE.INCOME} />
        <SelectSubgrupoContabil setValues={setValues} accountSubgroup={values?.accountSubgroup ?? null} accountGroup={values?.accountGroup ?? null} isDelete={isDisabled} />
      </div>
      <div className='linha-campos'>
        <TextField
          label="Pagador"
          id='payer'
          name='payer'
          value={values.payer ?? ''}
          variant="outlined"
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
          fullWidth
          disabled={isDisabled}
        />
        <MaskedTextField
          mask={
            values?.cpfCnpj && values?.cpfCnpj?.replace(/[^\d]/g, '').length < 12
              ? '999.999.999-999'
              : '99.999.999/9999-99'
          }
          label="CPF/CNPJ"
          id='cpfCnpj'
          name='cpfCnpj'
          value={values.cpfCnpj ?? ''}
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          onChange={handleChange}
          varibleName='cpfCnpj'
          fullWidth
          style={{ margin: '0' }}
          disabled={isDisabled}
        />
      </div>
      <div className='linha-campos'>
        <NumericFormat
          customInput={TextField}
          label="Valor"
          id='amount'
          name='amount'
          value={values.amount}
          variant="outlined"
          InputProps={{
            startAdornment: <InputAdornment position="start">R$</InputAdornment>,
          }}
          fullWidth
          disabled={isDisabled}
          decimalSeparator=","
          decimalScale={2}
          autoComplete="off"
          onChange={handleChange}
          required
        />
        <TextField
          label="Data de Vencimento"
          id='dueDate'
          name='dueDate'
          value={values.dueDate}
          variant="outlined"
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
          required
          fullWidth
          disabled={isDisabled}
          type='date'
        />
        <TextField
          label="Parcelas (meses)"
          type="number"
          value={installmentsCount}
          variant="outlined"
          onChange={handleChangeInstallmentCounts}
          inputProps={{
            min: 1,
            step: 1,
          }} fullWidth
        />
      </div>

      {formatNumber(installmentsCount) === formatNumber(1) && (
        <div className='linha-campos'>
          <NumericFormat
            customInput={TextField}
            label="Valor Pago"
            id='amountPayed'
            name='amountPayed'
            value={values.amountPayed ?? ''}
            variant="outlined"
            onChange={handleChangePaid}
            InputProps={{
              startAdornment: <InputAdornment position="start">R$</InputAdornment>,
            }}
            fullWidth
            disabled={isDisabled}
            decimalSeparator=","
            decimalScale={2}
            autoComplete="off"
            required={paymentFieldsRequired}
          />
          <TextField
            label="Data do Pagamento"
            id='receiptDate'
            name='receiptDate'
            value={values.receiptDate ?? ''}
            variant="outlined"
            onChange={handleChangePaid}
            InputLabelProps={{ shrink: true }}
            fullWidth
            disabled={isDisabled}
            type='date'
            required={paymentFieldsRequired}
          />
        </div>
      )}

      {installmentsCount && formatNumber(installmentsCount) > formatNumber(1) && (
        <InstallmentList installments={values?.billingInstallments} setValues={setValues} />
      )}

      <div className='linha-campos'>
        <FormControlLabel
          control={
            <Checkbox
              checked={values.showReportClosing}
              onChange={(e) => setValues({ ...values, showReportClosing: e.target.checked })}
              color="default"
            />
          }
          label="Exibir nos relatórios de fechamento"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={values.showReportEvolution}
              onChange={(e) => setValues({ ...values, showReportEvolution: e.target.checked })}
              color="default"
            />
          }
          label="Exibir no relatório de evolução patrimonial"
        />
      </div>
    </CrudFormPage>
  )
};

FormContasAReceber.propTypes = {
  values: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([undefined])]),
    name: PropTypes.string.isRequired,
    amount: PropTypes.string.isRequired,
    dueDate: PropTypes.string.isRequired,
    payer: PropTypes.string,
    cpfCnpj: PropTypes.string,
    amountPayed: PropTypes.string,
    receiptDate: PropTypes.string,
    showReportClosing: PropTypes.bool,
    showReportEvolution: PropTypes.bool,
    accountGroup: PropTypes.oneOfType([
      PropTypes.shape(AccountGroupPropTypes),
      PropTypes.oneOf([null, undefined]),
    ]),
    accountGroupId: PropTypes.number,
    accountSubgroup: PropTypes.oneOfType([
      PropTypes.shape(AccountSubgroupPropTypes),
      PropTypes.oneOf([null, undefined]),
    ]),
    accountSubgroupId: PropTypes.number,
    billingInstallments: PropTypes.arrayOf(PropTypes.shape(BillingInstallmentPropTypes)),
  }).isRequired,
  setValues: PropTypes.func.isRequired,
  isDelete: PropTypes.bool.isRequired,
  isLoadingSubmit: PropTypes.bool.isRequired,
}

export default FormContasAReceber;